import { useState, useEffect } from 'react';
import { useResponsive } from '../hooks/useResponsive';
import { useLocation, useNavigate } from 'react-router-dom';

import { View, Pressable } from 'react-native-web';
import CustomText from '../components/atoms/CustomText';
import Footer from '../components/layout/Footer';
import { colors, spacing, typography } from '../utils/styling';
import Button from '../components/common/Button';
import GeneralDialog from '../components/common/GeneralDialog';
import { downloadPDF, generatePDFBlob } from '../lib/pdf-utils';
import { sendPdfEmail, trackPDFDownload } from '../lib/stripe';
import { uploadPDFToSupabaseSimple } from '../lib/supabase-pdf';
import { CATEGORY_SECTIONS, SPECIFIC_CATEGORY_SECTIONS } from '../utils/category_mappings';
import { resolveWorkbookExercises } from '../utils/quizinfo/personalityContent';
import { loadDataWithVersionCheck } from "../utils/versionManager";
import { ResultsData } from '../utils/categoryMigration';
import { clearMoneyPersonalityAssessmentState } from '../utils/quizRetake';

import '../fonts/fonts.css';
import '../App.css';

const HERO_BG = 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)';
const SECTION_BG_ALT = '#f8f9fa';
const ACCENT = '#485A6B';

const AdvisorsHome = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useResponsive();

  const [showDialog, setShowDialog] = useState(false);
  const [dialogType, setDialogType] = useState<'download' | 'info-collection' | 'main-results'>('download');
  const [isEmailing, setIsEmailing] = useState<boolean>(false);
  const [isUser, setIsUser] = useState(false);
  const [isManualDownloading, setIsManualDownloading] = useState(false);
  const [transactionData, setTransactionData] = useState<{
    status: string;
    transaction_id: string | null;
    error?: string;
  } | null>(null);

  let resultsData = location.state as ResultsData | undefined;

  if (!resultsData) {
    const stored = localStorage.getItem("mpay_resultsData");
    if (stored) {
      try {
        let parsedData = JSON.parse(stored);
        parsedData = loadDataWithVersionCheck(parsedData, 'localStorage');
        resultsData = parsedData;
      } catch (e) {
        console.error("Error parsing/migrating localStorage data:", e);
      }
    }
  }

  const defaultResultsData: ResultsData = {
    answers: [],
    categoryScores: {},
    dominantCategory: "",
    sfAccount: null,
    quizId: undefined,
    leadId: undefined,
    email: undefined,
  };

  const safeResultsData = resultsData || defaultResultsData;

  const {
    email,
    dominantCategory,
    answers,
    categoryScores,
    specificAnswers,
    specificCategoryScores,
    specificDominantCategory,
    sfAccount,
    quizId,
  } = safeResultsData;


  const handleQuizNavigation = () => {
    const stored = localStorage.getItem('mpay_resultsData');
    if (stored) {
      const hasPaid = true;
      const personalInfo = localStorage.getItem('mpay_personalInfo');
      const hasCompletedInfoCollection = personalInfo !== null;
      const showDownload = hasPaid && hasCompletedInfoCollection;
      if (showDownload) {
        setDialogType('download');
        setShowDialog(true);
      } else if (hasPaid) {
        setDialogType('info-collection');
        setShowDialog(true);
      } else {
        setDialogType('main-results');
        setShowDialog(true);
      }
    } else {
      navigate('/quiz', { state: { from: location.pathname }} );
    }
  };

  const handleEmailPDF = async () => {
    setIsEmailing(true);
    try {
      const resultsData = JSON.parse(localStorage.getItem('mpay_resultsData') || '{}');
      const personalInfo = JSON.parse(localStorage.getItem('mpay_personalInfo') || '{}');
      if (!resultsData.email || !resultsData.quizId) return;
      let transactionId = "Client";
      if (transactionData?.status === 'found' && transactionData.transaction_id) {
        transactionId = transactionData.transaction_id;
      }
      const pdfBlob = await generatePDFBlob({
        answers: resultsData.answers || [],
        categoryScores: resultsData.categoryScores || {},
        dominantCategory: resultsData.dominantCategory || "",
        personalInfo: personalInfo
      });
      const emailHash = resultsData.email.replace(/[^a-zA-Z0-9]/g, '');
      const dateStr = new Date().toISOString().split('T')[0];
      const filename = `personality-profile-${emailHash}-${dateStr}`;
      const uploadResult = await uploadPDFToSupabaseSimple(pdfBlob, filename);
      if (resultsData.quizId && resultsData.email) {
        try {
          await trackPDFDownload(resultsData.quizId, resultsData.email, filename, uploadResult.url);
        } catch (trackError) {
          console.warn('PDF tracking failed:', trackError);
        }
      }
      await sendPdfEmail(
        resultsData.email, resultsData.quizId, resultsData, transactionId,
        CATEGORY_SECTIONS, SPECIFIC_CATEGORY_SECTIONS,
        resultsData.dominantCategory, resultsData.specificDominantCategory,
        uploadResult.url
      );
    } catch (error) {
      console.error('Failed to email PDF:', error);
    } finally {
      setIsEmailing(false);
    }
  }

  const handleDownloadPDF = async () => {
    setIsManualDownloading(true);
    try {
      const resultsData = JSON.parse(localStorage.getItem('mpay_resultsData') || '{}');
      const personalInfo = JSON.parse(localStorage.getItem('mpay_personalInfo') || '{}');
      const filename = `financial-personality-profile-${new Date().toISOString().split('T')[0]}`;
      await downloadPDF({
        answers: resultsData.answers || [],
        categoryScores: resultsData.categoryScores || {},
        dominantCategory: resultsData.dominantCategory || "",
        personalInfo: personalInfo,
        exercises: resolveWorkbookExercises(specificDominantCategory)
      }, filename);
    } catch (error) {
      console.error('Failed to download PDF:', error);
    } finally {
      setIsManualDownloading(false);
    }
  }

  const handleResultsNavigation = () => {
    const resultsData = JSON.parse(localStorage.getItem('mpay_resultsData') || '{}');
    navigate('/quiz/results-ready', { state: resultsData });
  }

  const handlePortalNavigation = () => {
    const resultsData = JSON.parse(localStorage.getItem('mpay_resultsData') || '{}');
    navigate('/login', { state: resultsData })
  }

  const handleRetakeQuiz = () => {
    clearMoneyPersonalityAssessmentState();
    setShowDialog(false);
    navigate('/quiz', { state: { from: location.pathname, retake: true } });
  };

  const renderRetakeButton = () => (
    <Pressable
      onPress={handleRetakeQuiz}
      style={({ hovered }: { hovered: boolean }) => ({
        backgroundColor: hovered ? '#f5f5f5' : 'white',
        paddingVertical: spacing.sm,
        paddingHorizontal: spacing.lg,
        border: '1px solid black',
        height: isMobile ? 40 : 50,
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      })}
    >
      <CustomText style={{ color: 'black', fontSize: 16, fontWeight: '600', fontFamily: 'Nunito' }}>
        Retake quiz
      </CustomText>
    </Pressable>
  );

  useEffect(() => {
    // @ts-ignore
    if (window.transactionData) {
      // @ts-ignore
      setTransactionData(window.transactionData);
    }
    const checkTransactionData = () => {
      // @ts-ignore
      if (window.transactionData && window.transactionData !== transactionData) {
        // @ts-ignore
        setTransactionData(window.transactionData);
      }
    };
    const interval = setInterval(checkTransactionData, 1000);
    return () => clearInterval(interval);
  }, [transactionData]);

  /* ─────────────── DATA ─────────────── */

  const PROCESS_STEPS = [
    {
      num: '01',
      title: 'Assess',
      desc: 'Clients take a quick personality assessment that reveals their behavioral relationship with money.',
    },
    {
      num: '02',
      title: 'Profile',
      desc: 'Each client receives a detailed personality profile with actionable planning insights.',
    },
    {
      num: '03',
      title: 'Advise',
      desc: 'Use the profile to personalize conversations, deepen trust, and drive better financial outcomes.',
    },
  ];

  const VALUE_PROPS = [
    {
      title: 'Client Profiling',
      desc: 'Understand every client before the first meeting with a behavioral money personality profile.',
      icon: '📊',
    },
    {
      title: 'Lead Generation',
      desc: 'Embed a public-facing quiz on your site to capture qualified leads and convert prospects.',
      icon: '🧲',
    },
    {
      title: 'Planning Insights',
      desc: 'Get personality-specific tips and talking points to tailor each financial plan.',
      icon: '💡',
    },
    {
      title: 'Marketing Kit',
      desc: 'QR codes, social graphics, and flyers your team can download and use immediately.',
      icon: '📣',
    },
    {
      title: 'Printable Reports',
      desc: 'Generate detailed and overview PDF reports to review before client meetings.',
      icon: '📄',
    },
    {
      title: 'Team Dashboard',
      desc: 'A centralized portal where every advisor on your team can access client profiles and tools.',
      icon: '👥',
    },
  ];

  /* ─────────────── RENDER ─────────────── */

  return (
    <View style={{ flex: 1, backgroundColor: colors.white, minHeight: '100vh', position: 'relative', alignItems: 'center', width: '100%' }}>

      {/* ══════════ HERO ══════════ */}
      <div style={{
        width: '100%',
        background: HERO_BG,
        paddingTop: isMobile ? 120 : 160,
        paddingBottom: isMobile ? 60 : 100,
        paddingLeft: 24,
        paddingRight: 24,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
      }}>
        <p style={{
          margin: 0,
          fontFamily: typography.fontFamily.body,
          fontSize: isMobile ? 14 : 15,
          fontWeight: 600,
          letterSpacing: '0.08em',
          textTransform: 'uppercase' as const,
          color: ACCENT,
          marginBottom: isMobile ? 16 : 20,
        }}>
          Built for Financial Advisory Teams
        </p>

        <h1 style={{
          margin: 0,
          fontFamily: typography.fontFamily.body,
          fontSize: isMobile ? 34 : 56,
          fontWeight: 700,
          color: colors.dark,
          lineHeight: 1.15,
          maxWidth: 800,
        }}>
          Know your clients before the first conversation
        </h1>

        <p style={{
          margin: 0,
          marginTop: isMobile ? 16 : 24,
          fontFamily: typography.fontFamily.body,
          fontSize: isMobile ? 16 : 19,
          color: colors.gray[600],
          lineHeight: 1.6,
          maxWidth: 620,
        }}>
          Money Personality gives advisory teams a behavioral profiling system that deepens
          client relationships, drives better plans, and generates qualified leads.
        </p>

        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 12 : 16,
          marginTop: isMobile ? 32 : 40,
          width: isMobile ? '100%' : undefined,
          maxWidth: isMobile ? 340 : undefined,
          alignItems: 'center',
        }}>
          <Button
            variant="primary"
            label="Book a demo"
            style={{
              paddingHorizontal: 32,
              paddingVertical: 16,
              borderRadius: 8,
              backgroundColor: ACCENT,
              width: isMobile ? '100%' : undefined,
            }}
            textStyle={{ fontFamily: typography.fontFamily.body, fontSize: 17, fontWeight: '700' }}
            onPress={() => navigate('/demo')}
          />
          <Button
            variant="secondary"
            label="Sign in"
            style={{
              paddingHorizontal: 32,
              paddingVertical: 16,
              borderRadius: 8,
              backgroundColor: 'transparent',
              border: `2px solid ${ACCENT}`,
              width: isMobile ? '100%' : undefined,
            }}
            textStyle={{ fontFamily: typography.fontFamily.body, fontSize: 17, fontWeight: '700', color: ACCENT }}
            onPress={() => navigate('/login')}
          />
        </div>
      </div>

      {/* ══════════ HOW IT WORKS ══════════ */}
      <div style={{
        width: '100%',
        maxWidth: 1100,
        padding: isMobile ? '48px 24px' : '80px 24px',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        margin: '0 auto',
      }}>
        <h2 style={{
          margin: 0,
          fontFamily: typography.fontFamily.body,
          fontSize: isMobile ? 26 : 36,
          fontWeight: 700,
          color: colors.dark,
          textAlign: 'center',
        }}>
          How it works
        </h2>
        <p style={{
          margin: 0,
          marginTop: 12,
          fontFamily: typography.fontFamily.body,
          fontSize: isMobile ? 15 : 17,
          color: colors.gray[600],
          textAlign: 'center',
          maxWidth: 560,
        }}>
          A simple three-step process that transforms how your team understands and serves clients.
        </p>

        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 32 : 48,
          marginTop: isMobile ? 36 : 56,
          width: '100%',
          justifyContent: 'center',
        }}>
          {PROCESS_STEPS.map((step) => (
            <div key={step.num} style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: isMobile ? 'center' : 'flex-start',
              textAlign: isMobile ? 'center' : 'left',
              maxWidth: isMobile ? undefined : 320,
            }}>
              <span style={{
                fontFamily: typography.fontFamily.body,
                fontSize: 48,
                fontWeight: 800,
                color: colors.gray[200],
                lineHeight: 1,
              }}>
                {step.num}
              </span>
              <h3 style={{
                margin: 0,
                marginTop: 12,
                fontFamily: typography.fontFamily.body,
                fontSize: 22,
                fontWeight: 700,
                color: colors.dark,
              }}>
                {step.title}
              </h3>
              <p style={{
                margin: 0,
                marginTop: 8,
                fontFamily: typography.fontFamily.body,
                fontSize: 15,
                lineHeight: 1.6,
                color: colors.gray[600],
              }}>
                {step.desc}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* ══════════ VALUE PROPS ══════════ */}
      <div style={{
        width: '100%',
        backgroundColor: SECTION_BG_ALT,
        padding: isMobile ? '48px 24px' : '80px 24px',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}>
        <h2 style={{
          margin: 0,
          fontFamily: typography.fontFamily.body,
          fontSize: isMobile ? 26 : 36,
          fontWeight: 700,
          color: colors.dark,
          textAlign: 'center',
        }}>
          Everything your team needs
        </h2>
        <p style={{
          margin: 0,
          marginTop: 12,
          fontFamily: typography.fontFamily.body,
          fontSize: isMobile ? 15 : 17,
          color: colors.gray[600],
          textAlign: 'center',
          maxWidth: 560,
        }}>
          One platform for profiling, planning, lead generation, and marketing.
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
          gap: isMobile ? 20 : 28,
          marginTop: isMobile ? 36 : 56,
          maxWidth: 1100,
          width: '100%',
        }}>
          {VALUE_PROPS.map((prop) => (
            <div key={prop.title} style={{
              backgroundColor: colors.white,
              borderRadius: 12,
              padding: isMobile ? 24 : 28,
              border: `1px solid ${colors.gray[200]}`,
              display: 'flex',
              flexDirection: 'column',
            }}>
              <span style={{ fontSize: 32, marginBottom: 12 }}>{prop.icon}</span>
              <h3 style={{
                margin: 0,
                fontFamily: typography.fontFamily.body,
                fontSize: 18,
                fontWeight: 700,
                color: colors.dark,
              }}>
                {prop.title}
              </h3>
              <p style={{
                margin: 0,
                marginTop: 8,
                fontFamily: typography.fontFamily.body,
                fontSize: 15,
                lineHeight: 1.6,
                color: colors.gray[600],
              }}>
                {prop.desc}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* ══════════ TESTIMONIALS PLACEHOLDER ══════════ */}
      <div style={{
        width: '100%',
        maxWidth: 1100,
        padding: isMobile ? '48px 24px' : '80px 24px',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        margin: '0 auto',
      }}>
        <h2 style={{
          margin: 0,
          fontFamily: typography.fontFamily.body,
          fontSize: isMobile ? 26 : 36,
          fontWeight: 700,
          color: colors.dark,
          textAlign: 'center',
        }}>
          What advisors are saying
        </h2>

        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 20 : 28,
          marginTop: isMobile ? 32 : 48,
          width: '100%',
          justifyContent: 'center',
        }}>
          {[1, 2, 3].map((i) => (
            <div key={i} style={{
              flex: 1,
              maxWidth: isMobile ? undefined : 340,
              backgroundColor: colors.gray[100],
              borderRadius: 12,
              padding: isMobile ? 24 : 28,
              border: `1px solid ${colors.gray[200]}`,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 140,
            }}>
              <p style={{
                margin: 0,
                fontFamily: typography.fontFamily.body,
                fontSize: 15,
                color: colors.gray[500],
                fontStyle: 'italic',
                textAlign: 'center',
              }}>
                Testimonial coming soon
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* ══════════ INDIVIDUAL QUIZ CTA ══════════ */}
      <div style={{
        width: '100%',
        backgroundColor: SECTION_BG_ALT,
        padding: isMobile ? '48px 24px' : '64px 24px',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
      }}>
        <h2 style={{
          margin: 0,
          fontFamily: typography.fontFamily.body,
          fontSize: isMobile ? 22 : 28,
          fontWeight: 700,
          color: colors.dark,
        }}>
          Curious about your own money personality?
        </h2>
        <p style={{
          margin: 0,
          marginTop: 12,
          fontFamily: typography.fontFamily.body,
          fontSize: isMobile ? 15 : 17,
          color: colors.gray[600],
          maxWidth: 480,
        }}>
          Take the assessment yourself in about five minutes.
        </p>
        <Button
          variant="primary"
          label="Take the quiz"
          style={{
            marginTop: 24,
            paddingHorizontal: 32,
            paddingVertical: 14,
            borderRadius: 8,
            backgroundColor: '#000',
          }}
          textStyle={{ fontFamily: typography.fontFamily.body, fontSize: 16, fontWeight: '700' }}
          onPress={handleQuizNavigation}
        />
      </div>

      {/* ══════════ BOTTOM CTA ══════════ */}
      <div style={{
        width: '100%',
        background: `linear-gradient(135deg, ${ACCENT} 0%, #374957 100%)`,
        padding: isMobile ? '56px 24px' : '80px 24px',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
      }}>
        <h2 style={{
          margin: 0,
          fontFamily: typography.fontFamily.body,
          fontSize: isMobile ? 26 : 36,
          fontWeight: 700,
          color: colors.white,
        }}>
          Ready to see it in action?
        </h2>
        <p style={{
          margin: 0,
          marginTop: 12,
          fontFamily: typography.fontFamily.body,
          fontSize: isMobile ? 15 : 17,
          color: 'rgba(255,255,255,0.8)',
          maxWidth: 480,
        }}>
          Book a demo and see how Money Personality can work for your advisory team.
        </p>
        <Button
          variant="primary"
          label="Book a demo"
          style={{
            marginTop: 28,
            paddingHorizontal: 36,
            paddingVertical: 16,
            borderRadius: 8,
            backgroundColor: colors.white,
          }}
          textStyle={{ fontFamily: typography.fontFamily.body, fontSize: 17, fontWeight: '700', color: ACCENT }}
          onPress={() => navigate('/demo')}
        />
      </div>

      <Footer />

      {/* ═══ Quiz retake dialogs (preserved from existing flow) ═══ */}
      {showDialog && dialogType === 'download' && (
        <GeneralDialog
          title="Wait... You look familiar!"
          description="It appears you have already taken the Money Personality Assessment. You can download your personalized PDF report or have it emailed to you. You can also login to the portal."
          yesText="Close"
          noText=""
          onConfirm={() => setShowDialog(false)}
          onClose={() => setShowDialog(false)}
        >
          <View style={{ display: 'flex', flexDirection: 'column', gap: spacing.md, marginTop: spacing.md }}>
            <Pressable
              onPress={handleDownloadPDF}
              disabled={isManualDownloading}
              style={({ hovered }: { hovered: boolean }) => ({
                backgroundColor: isManualDownloading ? colors.gray[400] : (hovered ? '#515A64' : 'black'),
                paddingVertical: spacing.sm,
                paddingHorizontal: spacing.lg,
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: isMobile ? 40 : 50,
                cursor: isManualDownloading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                opacity: isManualDownloading ? 0.7 : 1,
              })}
            >
              <CustomText style={{ color: colors.white, fontSize: 16, fontWeight: '600', fontFamily: 'Nunito' }}>
                {isManualDownloading ? 'Generating PDF...' : 'Download PDF to my device'}
              </CustomText>
            </Pressable>
            <Pressable
              onPress={handleEmailPDF}
              disabled={isEmailing}
              style={({ hovered }: { hovered: boolean }) => ({
                backgroundColor: isEmailing ? colors.gray[400] : (hovered ? '#f5f5f5' : 'white'),
                paddingVertical: spacing.sm,
                paddingHorizontal: spacing.lg,
                border: '1px solid black',
                height: isMobile ? 40 : 50,
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: isEmailing ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                opacity: isEmailing ? 0.7 : 1,
              })}
            >
              <CustomText style={{ color: 'black', fontSize: 16, fontWeight: '600', fontFamily: 'Nunito' }}>
                {isEmailing ? 'Emailing PDF...' : 'Email me my PDF'}
              </CustomText>
            </Pressable>
            <Pressable
              onPress={handlePortalNavigation}
              disabled={isEmailing}
              style={({ hovered }: { hovered: boolean }) => ({
                backgroundColor: isEmailing ? colors.gray[400] : (hovered ? '#f5f5f5' : 'white'),
                paddingVertical: spacing.sm,
                paddingHorizontal: spacing.lg,
                border: '1px solid black',
                height: isMobile ? 40 : 50,
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: isEmailing ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                opacity: isEmailing ? 0.7 : 1,
              })}
            >
              <CustomText style={{ color: 'black', fontSize: 16, fontWeight: '600', fontFamily: 'Nunito' }}>
                Portal Login
              </CustomText>
            </Pressable>
            {renderRetakeButton()}
          </View>
        </GeneralDialog>
      )}

      {showDialog && dialogType === 'info-collection' && (
        <GeneralDialog
          title="Final Steps"
          description="You have already taken the Money Personality assessment. To unlock your personalized PDF report, please complete a quick information collection form in your portal. This will only take about 1 minute."
          yesText="Take me there"
          noText="Cancel"
          onConfirm={handlePortalNavigation}
          onClose={() => setShowDialog(false)}
        >
          <View style={{ display: 'flex', alignItems: 'center', marginTop: spacing.md, marginBottom: spacing.sm }}>
            <CustomText style={{ fontSize: 14, color: colors.gray[600], fontFamily: 'Nunito', textAlign: 'center' }}>
              Your results are ready and waiting for you!
            </CustomText>
            <View style={{ width: '100%', marginTop: spacing.md }}>
              {renderRetakeButton()}
            </View>
          </View>
        </GeneralDialog>
      )}

      {showDialog && dialogType === 'main-results' && (
        <GeneralDialog
          title="Gain Full Access"
          description="It appears you have taken the Money Personality Quiz already. To view your initial results, please click the button below."
          yesText="Take me to my results"
          noText="Cancel"
          onConfirm={handleResultsNavigation}
          onClose={() => setShowDialog(false)}
        >
          <View style={{ display: 'flex', alignItems: 'center', marginTop: spacing.md, marginBottom: spacing.sm }}>
            <CustomText style={{ fontSize: 14, color: colors.gray[600], fontFamily: 'Nunito', textAlign: 'center' }}>
              Your results are ready and waiting for you!
            </CustomText>
            <View style={{ width: '100%', marginTop: spacing.md }}>
              {renderRetakeButton()}
            </View>
          </View>
        </GeneralDialog>
      )}
    </View>
  );
};

export default AdvisorsHome;
